import type { CreateJobInput } from '@chukua/common';
import { FileX, ShieldAlert } from 'lucide-react';
import { Navigate, useParams } from 'react-router';
import { DetailPageSkeleton } from '../../../../../shared/components/DetailPageSkeleton';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { PageHeader } from '../../../../../shared/components/PageHeader';
import { sectionTrail } from '../../../../../shared/components/section-trail';
import { ContainerDetailsSection } from '../../cargo/components/ContainerDetailsSection';
import { JobAttachments } from '../../uploads/components/JobAttachments';
import { JobIntakeForm } from '../components/JobIntakeForm';
import { JobIntakeMissingItems } from '../components/JobIntakeMissingItems';
import { JobIntakeReadiness } from '../components/JobIntakeReadiness';
import { LooseCargoSection } from '../../cargo/components/LooseCargoSection';
import { useJobCatalogues, type JobCataloguesState } from '../../../hooks/use-job-catalogues';
import { useJobRecord } from '../../../hooks/use-job-record';

const JOBS_TRAIL = sectionTrail('jobs');

/** Empty catalogues while they're still loading or failed — the page renders with nothing to pick from rather than waiting. */
function resolvedCatalogues(catalogues: JobCataloguesState) {
  return catalogues.status === 'loaded' ? catalogues : { jobTypes: [], containerSizes: [], containerTypes: [], specialHandlingRequirements: [] };
}

/** `/jobs` (quick-create) and `/jobs/:id` (reopen to finish or check). Keyed by id, so moving between jobs starts fresh. */
export function JobIntakePage() {
  const { id } = useParams();
  return <JobIntake key={id ?? 'new'} jobId={id} />;
}

function JobIntake({ jobId }: { jobId: string | undefined }) {
  const { state, saveError, saving, create, update, reload } = useJobRecord(jobId);
  const catalogues = useJobCatalogues();

  if (state.status === 'forbidden') return <Navigate to="/" replace />;
  if (state.status === 'loading') return <DetailPageSkeleton label="Loading job" breadcrumbs={JOBS_TRAIL} tabs={0} />;
  if (state.status === 'not_found') {
    return <EmptyState icon={FileX} title="Job not found" description="This job doesn't exist, or the link is out of date." />;
  }
  if (state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load this job" description="Something went wrong loading this job. Try reloading the page." />;
  }

  const { job } = state;
  const { jobTypes, containerSizes, containerTypes, specialHandlingRequirements } = resolvedCatalogues(catalogues);

  async function handleSave(input: CreateJobInput): Promise<void> {
    if (job) await update(input);
    else await create(input);
  }

  return (
    <>
      <PageHeader
        breadcrumbs={JOBS_TRAIL}
        title={job ? job.number : 'New job'}
        description="Customer and job type are enough to start — everything else can be filled in as you learn it."
      />
      {job && <JobIntakeMissingItems job={job} jobTypes={jobTypes} />}
      {job && <JobIntakeReadiness job={job} />}
      <JobIntakeForm job={job} jobTypes={jobTypes} error={saveError} saving={saving} onSave={handleSave} />
      <ContainerDetailsSection job={job} jobTypes={jobTypes} containerSizes={containerSizes} containerTypes={containerTypes} onChanged={reload} />
      <LooseCargoSection job={job} jobTypes={jobTypes} specialHandlingRequirements={specialHandlingRequirements} onChanged={reload} />
      {job && <JobAttachments jobId={job.id} attachments={job.attachments} onUploaded={reload} />}
    </>
  );
}
